import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import PersonnelProfileLayout from './PersonnelProfileLayout';

// import utility file
import { Alert, Button, Card, Col, Form, Row } from 'react-bootstrap';
import { ExclamationTriangleFill } from 'react-bootstrap-icons';

import PersonnelService from '../../services/PersonnelService';
import ContratService from '../../services/ContratService';

const ViewContrat = () => {
	const { id } = useParams();
	const [personnel, setPersonnel] = useState(null);
	const [contrat, setContrat] = useState(null);
	const [validated, setValidated] = useState(false);
	const [feedbackMessages, setFeedbackMessages] = useState({});
	const [showAlert, setShowAlert] = useState(false);
	const [alertMessage, setAlertMessage] = useState('');
	const [selectedType, setSelectedType] = useState('');

	const TYPE_CHOICES = [
		{ value: 'cdi', label: 'CDI' },
		{ value: 'cdd', label: 'CDD' },
		{ value: 'stage', label: 'Stage' }
	];

	useEffect(() => {
		const fetchData = async () => {
			const personnelData = await PersonnelService.getPersonnel(id);

			setPersonnel(personnelData.data);
			setContrat(personnelData.data.dernier_contrat);

			const contratType = personnelData.data.dernier_contrat.type;
			setSelectedType(contratType);
		};

		fetchData();
	}, [id]);

	const handleSubmit = async (event) => {
		event.preventDefault();
		const form = event.currentTarget;

		if (form.checkValidity() === false) {
			event.stopPropagation();
		} else {
			try {
				const contratData = new FormData();

				// Add all your form data to the FormData
				contratData.append('type', form.type.value);
				contratData.append('date_debut', form.date_debut.value);
				contratData.append('date_fin', form.date_fin.value);
				contratData.append('salaire_brut', form.salaire_brut.value);
				contratData.append('personnel', id);

				// Call the PersonnelService to create the personnel
				// Call the EleveService to create the eleve
				const response = await ContratService.updateContrat(contrat.id, contratData);

				// Reset the form and state variables after successful update
				form.reset();
				setValidated(false);
				setFeedbackMessages({});

				// Add this line to refresh the page
				window.location.reload();
			} catch (error) {
				console.error(
					'Erreur lors de la mise à jour du contrat du personnel',
					error
				);

				if (error.response && error.response.data) {
					const apiErrors = error.response.data;

					// If there are field-specific errors
					if (typeof apiErrors === 'object' && !Array.isArray(apiErrors)) {
						const newFeedbackMessages = {};

						for (const field in apiErrors) {
							newFeedbackMessages[field] = apiErrors[field][0];
						}

						setFeedbackMessages(newFeedbackMessages);
					}

					// If there's a general error detail message
					if (apiErrors.detail) {
						setAlertMessage(apiErrors.detail[0]);
						setShowAlert(true);

						// Hide the alert after 5 seconds
						setTimeout(() => {
							setShowAlert(false);
						}, 10000);
					} else if (
						apiErrors['Il ya erreur'] &&
						apiErrors['Il ya erreur'][0]
					) {
						setAlertMessage(apiErrors['Il ya erreur'][0]);
						setShowAlert(true);

						// Hide the alert after 5 seconds
						setTimeout(() => {
							setShowAlert(false);
						}, 10000);
					}
				} else {
					alert(
						"Une erreur s'est produite lors de la soumission du formulaire."
					);
				}
			}
		}

		setValidated(true);
	};

	return (
		<PersonnelProfileLayout>
			{personnel && (
				<>
					<Card className="mb-3 ">
						{/* Card body */}
						<Card.Body>
							{/*  Form  */}
							<Form
								className="mb-3"
								noValidate
								validated={validated}
								onSubmit={handleSubmit}
							>
								{showAlert && (
									<Alert variant="danger">
										<ExclamationTriangleFill size={25} className="me-1" />
										{alertMessage}
									</Alert>
								)}
								<Row>
									{/* Type */}
									<Col xs={12} className="mb-3">
										<Form.Group className="mb-3">
											<Form.Label htmlFor="type">
												Type de contrat <span className="text-danger">*</span>
											</Form.Label>
											<Form.Select
												name="type"
												required
												defaultValue={selectedType}
												isInvalid={!!feedbackMessages['type']}
												onChange={(e) => {
													setFeedbackMessages((prevMessages) => ({
														...prevMessages,
														type: e.target.value
															? ''
															: 'Le type contrat est requis.'
													}));
												}}
											>
												<option value="">
													Selectionner un type de contrat
												</option>
												{TYPE_CHOICES.map((option, index) => (
													<option key={index} value={option.value}>
														{option.label}
													</option>
												))}
											</Form.Select>
											<Form.Control.Feedback type="invalid">
												{feedbackMessages['type']}
											</Form.Control.Feedback>
										</Form.Group>
									</Col>

									<Col xs={12} className="mb-3">
										{/* Date début et fin  */}
										<Form.Group>
											<Row>
												<Col md={6} sm={12} className="mb-3">
													<Form.Label htmlFor="date_debut">
														Date début <span className="text-danger">*</span>
													</Form.Label>
													<Form.Control
														name="date_debut"
														type="date"
														placeholder="Selectionner la date de début"
														id="date_debut"
														required
														isInvalid={!!feedbackMessages['date_debut']}
														onChange={(e) => {
															setFeedbackMessages((prevMessages) => ({
																...prevMessages,
																date_debut: e.target.value
																	? ''
																	: 'La date de début de ce contrat est requise.'
															}));
														}}
														defaultValue={personnel.dernier_contrat.date_debut}
													/>
													<Form.Control.Feedback type="invalid">
														{feedbackMessages['date_debut']}
													</Form.Control.Feedback>
												</Col>
												<Col md={6} sm={12} className="mb-3">
													<Form.Label htmlFor="date_fin">Date fin</Form.Label>
													<Form.Control
														name="date_fin"
														type="date"
														placeholder="Selectionner la date de fin"
														id="date_fin"
														isInvalid={!!feedbackMessages['date_fin']}
														onChange={(e) => {
															setFeedbackMessages((prevMessages) => ({
																...prevMessages,
																date_fin: e.target.value
																	? ''
																	: "La date selectionnée n'est pas valide."
															}));
														}}
														defaultValue={personnel.dernier_contrat.date_fin}
													/>
													<Form.Control.Feedback type="invalid">
														{feedbackMessages['date_fin']}
													</Form.Control.Feedback>
												</Col>
											</Row>
										</Form.Group>
									</Col>

									{/* Salaire brut */}
									<Col xs={12} className="mb-3">
										<Form.Group className="mb-3">
											<Form.Label htmlFor="salaire_brut">
												Salaire brut <span className="text-danger">*</span>
											</Form.Label>
											<Form.Control
												name="salaire_brut"
												type="number"
												min="25"
												placeholder="123456789 FCFA"
												id="salaire_brut"
												required
												isInvalid={!!feedbackMessages['salaire_brut']}
												onChange={(e) => {
													setFeedbackMessages((prevMessages) => ({
														...prevMessages,
														salaire_brut: e.target.value
															? ''
															: 'La valeur du salaire brut est requise.'
													}));
												}}
												defaultValue={personnel.dernier_contrat.salaire_brut}
											/>
											<Form.Control.Feedback type="invalid">
												{feedbackMessages['salaire_brut']}
											</Form.Control.Feedback>
										</Form.Group>
									</Col>

									{/* Buttons */}
									<Col xs={12} className="mt-4">
										<Button variant="success" type="submit">
											Mettre à jour
										</Button>
										<Button
											type="reset"
											variant="outline-danger"
											className="ms-2"
										>
											Effacer
										</Button>
									</Col>
								</Row>
							</Form>
						</Card.Body>
					</Card>
				</>
			)}
		</PersonnelProfileLayout>
	);
};

export default ViewContrat;
